import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, Calendar, IndianRupee, Users, FileText, Loader2, RefreshCw,
  TrendingUp, ChevronDown, ChevronUp, User, Stethoscope, ShoppingCart, Store
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorReferralLedger({ vendorToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedDoctor, setExpandedDoctor] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (doctorFilter) params.doctor_id = doctorFilter;
      if (sourceFilter) params.source_type = sourceFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const token = vendorToken || localStorage.getItem('vendor_token');
      const res = await axios.get(`${API_URL}/api/referral-ledger/vendor`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      console.error('Referral ledger error:', e);
    }
    setLoading(false);
  }, [search, doctorFilter, sourceFilter, dateFrom, dateTo, vendorToken]);

  useEffect(() => {
    const t = setTimeout(fetchData, 400);
    return () => clearTimeout(t);
  }, [fetchData]);

  return (
    <div className="space-y-4" data-testid="vendor-referral-ledger">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Referral Ledger</h2>
          <p className="text-xs text-slate-500">Doctor-wise commission tracking (no wallet impact)</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
      </div>

      {/* Grand Total */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-amber-700" style={{ fontFamily: 'Manrope' }}>
                  {data.grand_total?.toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-amber-600">Total Referral Charges</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-teal-700">{data.doctor_summaries?.length || 0}</p>
                <p className="text-xs text-teal-600">Doctors</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-violet-700">{data.total || 0}</p>
                <p className="text-xs text-violet-600">Total Entries</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="rl-search" placeholder="Search by doctor, order #..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" />
            </div>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="rl-source-filter">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="patient_order">Patient Orders</SelectItem>
                <SelectItem value="walkin_billing">Walk-in Billing</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-1.5 items-center">
              <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-32" />
              <span className="text-slate-400 text-xs">to</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-32" />
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" /></div>
      ) : !data || data.total === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No referral entries found</p>
        </div>
      ) : (
        <>
          {/* Doctor-wise Summary */}
          {data.doctor_summaries?.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold text-slate-700">Doctor-wise Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="space-y-2">
                  {data.doctor_summaries.map(ds => (
                    <div key={ds.doctor_id}
                      className={`rounded-lg border p-3 cursor-pointer transition-colors ${doctorFilter === ds.doctor_id ? 'border-teal-300 bg-teal-50' : 'border-slate-200 hover:bg-slate-50'}`}
                      onClick={() => setDoctorFilter(doctorFilter === ds.doctor_id ? '' : ds.doctor_id)}
                      data-testid={`rl-doctor-${ds.doctor_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <Stethoscope className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{ds.doctor_name}</p>
                            <p className="text-xs text-slate-500">{ds.doctor_specialty} &middot; {ds.order_count} orders</p>
                          </div>
                        </div>
                        <p className="font-bold text-amber-700" style={{ fontFamily: 'Manrope' }}>
                          <IndianRupee className="w-3.5 h-3.5 inline" />{ds.total_commission.toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Entries list */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-slate-700">Transaction Entries ({data.total})</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <div className="divide-y divide-slate-100">
                {data.entries.map((e, i) => (
                  <div key={`el-${i}`} className="py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${e.source_type === 'patient_order' ? 'bg-purple-100' : 'bg-orange-100'}`}>
                        {e.source_type === 'patient_order' ? <ShoppingCart className="w-4 h-4 text-purple-500" /> : <Store className="w-4 h-4 text-orange-500" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{e.doctor_name}</p>
                        <p className="text-xs text-slate-500">
                          {e.order_number} &middot; {e.source_type === 'patient_order' ? 'Patient Order' : 'Walk-in'} &middot; {new Date(e.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-amber-700 text-sm" style={{ fontFamily: 'Manrope' }}>
                      <IndianRupee className="w-3 h-3 inline" />{e.charge_amount?.toLocaleString('en-IN')}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
